import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, User as UserIcon, Check, FileText, FolderKanban } from 'lucide-react';
import type { MailMessage } from '../../lib/stores/mail/types';
import { useLeadPipelineStore } from '../../lib/stores/leadPipelineStore';
import { PersonOnCompanyPrompt } from './link/PersonOnCompanyPrompt';
import { useProspectDraft } from './link/useProspectDraft';
import { CreateNewPanel } from './link/CreateNewPanel';
import { DrawerHeader, DrawerThreadContext } from './link/DrawerHeader';
import { ExistingSearchPanel, type LinkTarget } from './link/ExistingSearchPanel';
import { TabBar, SubTabs, type TabItem } from './link/DrawerControls';
import { useAnimatedDismiss } from './link/useAnimatedDismiss';
import { useLinkActions } from './link/useLinkActions';

export interface LinkableThread {
  threadId: string;
  messages: MailMessage[];
}

interface MailLinkEntityDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  threads: LinkableThread[];
  organizationId: string;
  userId: string;
}

type Target = LinkTarget;
type Mode = 'existing' | 'new';

// Snapshot del thread per la nota/aggiornamento email (prospect = controparte esterna)
export function buildPayload(t: LinkableThread) {
  const inbound = t.messages.find(m => !(m.label_ids || []).includes('SENT'));
  const prospect = inbound
    ? { email: inbound.from_email || '', name: inbound.from_name || null, snippet: inbound.snippet || null }
    : {
        email: t.messages[0]?.to_emails?.[0]?.email || t.messages[0]?.from_email || '',
        name: t.messages[0]?.to_emails?.[0]?.name || null,
        snippet: t.messages[0]?.snippet || null,
      };
  return {
    gmailThreadId: t.threadId,
    subject: t.messages[0]?.subject || '(Nessun oggetto)',
    fromEmail: prospect.email,
    fromName: prospect.name,
    snippet: prospect.snippet,
    messageCount: t.messages.length,
  };
}

const TARGETS: TabItem<Target>[] = [
  { key: 'client', label: 'Cliente/Lead', icon: <UserIcon className="h-3.5 w-3.5" /> },
  { key: 'deal', label: 'Offerta', icon: <FileText className="h-3.5 w-3.5" /> },
  { key: 'project', label: 'Progetto', icon: <FolderKanban className="h-3.5 w-3.5" /> },
];

const MODES: TabItem<Mode>[] = [
  { key: 'existing', label: 'Associa esistente' },
  { key: 'new', label: 'Crea nuovo' },
];

export function MailLinkEntityDrawer({ isOpen, onClose, threads, organizationId, userId }: MailLinkEntityDrawerProps) {
  const { pipelines, fetchPipelines } = useLeadPipelineStore();

  const [target, setTarget] = useState<Target>('client');
  const [mode, setMode] = useState<Mode>('existing');
  const [pipelineId, setPipelineId] = useState('');

  // Chiusura animata: il pannello entra scorrendo, quindi deve anche uscire cosi`.
  const { dismiss, panelClass, scrimClass } = useAnimatedDismiss(isOpen, onClose);

  const single = threads.length === 1;
  const prospect = useMemo(() => (single ? buildPayload(threads[0]) : null), [threads, single]);

  // Chiave stabile: cambia solo quando cambiano davvero i thread da collegare.
  // L'identita` dell'array `threads` cambia a ogni render del padre, quindi non puo`
  // essere usata come dipendenza: rimetterebbe il form su "Associa esistente" a ogni
  // aggiornamento della posta, azzerando quello che l'utente sta scrivendo.
  const threadKey = threads.map(t => t.threadId).join('|');

  // Dati del prospect: firma, sito, VIES e controllo anti-doppione.
  const draft = useProspectDraft({
    isOpen,
    active: mode === 'new' && target === 'client' && single,
    threadKey,
    messages: single ? threads[0].messages : [],
    fromEmail: prospect?.fromEmail || null,
    fromName: prospect?.fromName || null,
    organizationId,
  });

  const actions = useLinkActions({
    threads, organizationId, userId, target, draft, pipelineId, buildPayload, dismiss,
  });
  const { linking, prompt, setPrompt, handleSelect, handleConfirmPerson, handleCreateAndLink } = actions;

  // Ref sempre aggiornata: legge il valore corrente senza entrare nelle dipendenze.
  const linkingRef = useRef(linking);
  linkingRef.current = linking;

  useEffect(() => {
    if (!isOpen) return;
    if (linkingRef.current) return; // mai resettare mentre stiamo creando o collegando
    fetchPipelines();
    setTarget('client');
    setMode('existing');
    setPipelineId('');
    setPrompt(null);
  }, [isOpen, threadKey, fetchPipelines, setPrompt]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, dismiss]);

  // Offerte e progetti non hanno il tab di creazione.
  useEffect(() => { if (target !== 'client') setMode('existing'); }, [target]);

  if (!isOpen) return null;

  return (
    <>
      {/* z-68/69: sopra il compose (z-66/67, può essere aperto/minimizzato mentre si
          collega un thread) e sotto MailTaskModal (z-100/101). */}
      <div className={`fixed inset-0 z-[68] bg-black/20 backdrop-blur-[1px] md:top-12 ${scrimClass}`} onClick={dismiss} />
      <div className={`fixed top-0 right-0 bottom-0 z-[69] md:top-12 w-full max-w-md md:w-[460px] bg-white dark:bg-gray-900 border-l border-black/10 dark:border-gray-700 shadow-2xl overflow-hidden flex flex-col ${panelClass}`}>
        <DrawerHeader threadCount={threads.length} onClose={dismiss} />

        {single && prospect && (
          <DrawerThreadContext
            subject={prospect.subject}
            who={prospect.fromName || prospect.fromEmail}
            messageCount={prospect.messageCount}
          />
        )}

        {/* Selettore destinazione (nascosto mentre si decide sulla persona) */}
        {!prompt && (
          <div className="px-5 pt-3 border-b border-black/[0.06] dark:border-gray-700">
            <TabBar items={TARGETS} active={target} onChange={setTarget} />
          </div>
        )}

        {/* Sub-tab esistente/nuovo (solo Cliente + singolo thread) */}
        {!prompt && target === 'client' && single && (
          <div className="px-5 pt-2.5">
            <SubTabs items={MODES} active={mode} onChange={setMode} />
          </div>
        )}

        {/* Corpo. La chiave forza il rimontaggio a ogni cambio di sezione: l'animazione
            di entrata riparte e il passaggio si vede. */}
        <div key={prompt ? 'prompt' : `${target}:${mode}`} className="flex-1 overflow-y-auto px-5 py-4">
          {prompt ? (
            <PersonOnCompanyPrompt
              decision={prompt.decision}
              companyName={prompt.company.name}
              person={draft.person}
              onPersonChange={draft.setPersonField}
              existingPersonName={prompt.existingPerson?.business_name ?? null}
              busy={linking}
              onConfirm={handleConfirmPerson}
              onSkip={() => { setPrompt(null); dismiss(); }}
            />
          ) : mode === 'existing' ? (
            <ExistingSearchPanel
              target={target}
              canCreate={target === 'client' && single}
              linking={linking}
              onSelect={handleSelect}
              onSwitchToCreate={() => setMode('new')}
            />
          ) : (
            <CreateNewPanel
              draft={draft}
              pipelines={pipelines}
              pipelineId={pipelineId}
              onPipelineChange={setPipelineId}
            />
          )}
        </div>

        {/* Footer (solo "Crea nuovo") */}
        {mode === 'new' && !prompt && (
          <div className="px-5 py-4 border-t border-black/[0.06] dark:border-gray-700 flex items-center justify-end gap-2 bg-[#FBFBFA] dark:bg-gray-900/60">
            <button
              onClick={dismiss}
              className="px-4 py-2 text-sm text-[#787774] rounded-lg hover:text-[#37352F] dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06] transition-colors"
            >
              Annulla
            </button>
            <button
              onClick={handleCreateAndLink}
              disabled={linking}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#2383E2] text-white text-sm font-medium rounded-lg shadow-sm shadow-[#2383E2]/25 hover:bg-[#1a6fc9] active:scale-[0.98] transition-all duration-100 disabled:opacity-50 disabled:active:scale-100"
            >
              {linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Crea e collega
            </button>
          </div>
        )}
      </div>
    </>
  );
}
